import { Lock, LogOut, Monitor, Moon, Sun, UserCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSessionIdentity, useSessionRole } from '@/auth/session-claims';
import { forceLogout } from '@/auth/force-logout';
import { lockScreen } from '@/auth/screen-lock';
import { useCachedAccess } from '@/lib/access/use-access';
import { roleLabel } from '@/lib/access/labels';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useColorScheme } from '@/theme/use-color-scheme';
import { type ThemePreference } from '@/theme/color-scheme';
import { cn } from '@/lib/cn';

// System first, and it is the default: an operator who has already told their OS
// they want dark at night should not have to tell the console too.
const APPEARANCES: ReadonlyArray<{
  value: ThemePreference;
  icon: typeof Monitor;
  labelKey: string;
}> = [
  { value: 'system', icon: Monitor, labelKey: 'avatar.appearanceSystem' },
  { value: 'light', icon: Sun, labelKey: 'avatar.appearanceLight' },
  { value: 'dark', icon: Moon, labelKey: 'avatar.appearanceDark' },
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '?';
  const second = parts.length > 1 ? parts[parts.length - 1]?.[0] : undefined;
  return `${first}${second ?? ''}`.toUpperCase();
}

export function AvatarMenu() {
  const { t } = useTranslation('admin');
  // Tolerant reads: the chrome also renders for a member with no access assigned, whose
  // session carries no role — it must show "no role", never crash (R5).
  const identity = useSessionIdentity();
  const legacyRole = useSessionRole();
  const access = useCachedAccess();
  const { preference, setPreference } = useColorScheme();
  const displayName = identity?.displayName ?? '';
  const operatorUrn = identity?.operatorUrn ?? '';
  // The operator's roles by name (system roles translated); the legacy role only while
  // `/v1/me` has not answered.
  const roles = access.ready
    ? access.roles.map((r) => roleLabel(t, r)).join(' · ') || t('access.noRoles')
    : legacyRole
      ? roleLabel(t, { key: legacyRole, name: legacyRole, system: true })
      : '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
        aria-label={t('avatar.accountMenu')}
      >
        {initialsOf(displayName)}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[14rem]">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="text-sm">{displayName}</span>
            <span className="text-xs font-normal text-muted-foreground" data-testid="avatar-roles">
              {roles}
            </span>
            <span className="mt-1 truncate text-[10px] font-mono text-muted-foreground">
              {operatorUrn}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => (window.location.href = '/profile')}>
          <UserCircle2 className="mr-2 h-4 w-4" />
          {t('common:nav.profile')}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {t('avatar.appearance')}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={preference}
          onValueChange={(value) => setPreference(value as ThemePreference)}
        >
          {APPEARANCES.map(({ value, icon: Icon, labelKey }) => (
            <DropdownMenuRadioItem key={value} value={value}>
              <Icon className="mr-2 h-4 w-4" />
              {t(labelKey)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => lockScreen()}>
          <Lock className="mr-2 h-4 w-4" />
          {t('avatar.lockScreen')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void forceLogout({ reason: 'user-initiated' })}>
          <LogOut className="mr-2 h-4 w-4" />
          {t('avatar.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
